import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { MainLayout } from '../components/MainLayout';
import { investmentsService, type ConsolidatedHolding } from '../services/investments-service';

export const ConsolidatedHoldingsPage: React.FC = () => {
  const [holdings, setHoldings] = useState<ConsolidatedHolding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sorting
  type SortField = 'symbol' | 'total_shares' | 'total_cost_basis' | 'acb_per_share' | 'account_count';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('symbol');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const fetchConsolidatedHoldings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await investmentsService.getConsolidatedHoldings();
      // Service now properly wraps response as ApiResponse<{ holdings: [], count: number }>
      let holdingsData = response?.data?.holdings || [];
      
      // Apply sorting
      holdingsData = [...holdingsData].sort((a: ConsolidatedHolding, b: ConsolidatedHolding) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortField) {
          case 'symbol':
            aValue = (a.symbol || '').toLowerCase();
            bValue = (b.symbol || '').toLowerCase();
            break;
          case 'total_shares':
            aValue = parseFloat(a.total_shares || '0');
            bValue = parseFloat(b.total_shares || '0');
            break;
          case 'total_cost_basis':
            aValue = parseFloat(a.total_cost_basis || '0');
            bValue = parseFloat(b.total_cost_basis || '0');
            break;
          case 'acb_per_share':
            aValue = parseFloat(a.acb_per_share || '0');
            bValue = parseFloat(b.acb_per_share || '0');
            break;
          case 'account_count':
            aValue = a.account_count || 0;
            bValue = b.account_count || 0;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      setHoldings(holdingsData);
    } catch (err: any) {
      console.error('Error fetching consolidated holdings:', err);
      // Extract error message from ApiError or generic Error
      let errorMessage = 'Failed to fetch consolidated holdings';
      if (err && typeof err === 'object') {
        if (err.message) {
          errorMessage = err.message;
        } else if (err.error) {
          errorMessage = err.error;
        } else if (err.details?.message) {
          errorMessage = err.details.message;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setHoldings([]);
    } finally {
      setLoading(false);
    }
  }, [sortField, sortDirection]);

  useEffect(() => {
    fetchConsolidatedHoldings();
  }, [fetchConsolidatedHoldings]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount: string, currency: string = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(amount || '0'));
  };

  const formatShares = (shares: string) => {
    return parseFloat(shares).toFixed(6);
  };

  // Calculate totals
  const totalCostBasis = holdings.reduce(
    (sum, holding) => sum + parseFloat(holding.total_cost_basis || '0'),
    0
  );
  const uniqueSymbols = holdings.length;

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Consolidated Holdings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all investment holdings consolidated by symbol across all accounts
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Positions
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {uniqueSymbols}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Cost Basis
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(totalCostBasis.toString())}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Holdings Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : holdings.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No consolidated holdings found. Add investment transactions to see consolidated holdings.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'symbol'}
                        direction={sortField === 'symbol' ? sortDirection : 'asc'}
                        onClick={() => handleSort('symbol')}
                      >
                        Symbol
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'total_shares'}
                        direction={sortField === 'total_shares' ? sortDirection : 'asc'}
                        onClick={() => handleSort('total_shares')}
                      >
                        Total Shares
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'total_cost_basis'}
                        direction={sortField === 'total_cost_basis' ? sortDirection : 'asc'}
                        onClick={() => handleSort('total_cost_basis')}
                      >
                        Total Cost Basis
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'acb_per_share'}
                        direction={sortField === 'acb_per_share' ? sortDirection : 'asc'}
                        onClick={() => handleSort('acb_per_share')}
                      >
                        ACB per Share
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'account_count'}
                        direction={sortField === 'account_count' ? sortDirection : 'asc'}
                        onClick={() => handleSort('account_count')}
                      >
                        Accounts
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Account Names</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holdings.map((holding) => (
                    <TableRow key={holding.symbol} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight={600}>
                          {holding.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontFamily="monospace">
                          {formatShares(holding.total_shares)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight={600}>
                          {formatCurrency(holding.total_cost_basis)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(holding.acb_per_share)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={holding.account_count}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {holding.account_names?.map((accountName, idx) => (
                            <Chip
                              key={idx}
                              label={accountName}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
};

